// [Project] > [Conversations] > [Debug: Scheduled] - the daily passes that worked
// this project. The table is shared with Conversations rather than copied, so
// the two tabs can't disagree about a row's label or link.
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useStatefulAgents, useStatefulRuns } from "../api/hooks/useStatefulAgents";
import { DateRangePicker, lastDays } from "../components/DateRangePicker";
import { RunNowPanel } from "../components/RunNowPanel";
import { agentLabelFrom } from "../utils/runParticipants";
import { projectScheduledPath } from "./learningAgentPaths";
import { ProjectRunsTable } from "./ProjectStatefulRunsTab";
import styles from "./ProjectStatefulRunsTab.module.css";

// A month, matching the specialist's own Scheduled tab, so "the last 30 days"
// means the same thing whichever page an operator is standing on.
const DEFAULT_DAYS = 30;

export const ProjectScheduledRunsTab = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const [range, setRange] = useState(() => lastDays(DEFAULT_DAYS));
  const roster = useStatefulAgents().data?.agents ?? [];
  const runsQuery = useStatefulRuns(decoded, range, "scheduled");
  const runs = runsQuery.data?.runs ?? [];
  const projectName = runsQuery.data?.project_name ?? "this site";

  return (
    <div className={styles.page}>
      <p className={styles.muted}>
        Every daily pass that worked this site. Opening one shows what it did
        here, with a link to the whole run and the other sites it worked.
      </p>
      {/* The same button the agent's own tab carries, scoped to this site: it
          works THIS project whether or not anything new arrived, which is the
          difference from the daily pass and the reason to have it here. */}
      <RunNowPanel
        agent={null}
        pageLabel="site's Scheduled"
        projectIds={[decoded]}
        buttonLabel="Run now on this site"
        note={
          "Runs the team over this site now, whether or not anything new has " +
          "arrived on it. It first checks Gmail, Drive and Slack for anything new. " +
          "If another run is already working this site, or as many as can run at " +
          "once already are, it is queued and starts by itself. The note is optional."
        }
        placeholder="e.g. the landlord came back on the lease - start there"
        runHref={(runId) => projectScheduledPath(decoded, runId)}
      />
      <DateRangePicker range={range} setRange={setRange} defaultDays={DEFAULT_DAYS} />
      {runsQuery.isLoading && <p className={styles.muted}>Loading…</p>}
      {runsQuery.error ? (
        <p className={styles.error}>Could not load this project&apos;s runs.</p>
      ) : null}
      {!runsQuery.isLoading && !runsQuery.error && runs.length === 0 && (
        <p className={styles.muted}>
          No scheduled run worked this site in this range. Scheduled runs are
          off until the project is enrolled with a specialist, on that
          agent&apos;s Portfolio tab.
        </p>
      )}
      {runs.length > 0 && (
        <ProjectRunsTable
          runs={runs}
          projectId={decoded}
          projectName={projectName}
          // A pass ends and records how it went, so its state is worth a column.
          showStatus
          showRunTime
          allowDelete={false}
          agentLabel={agentLabelFrom(roster)}
        />
      )}
    </div>
  );
};
